/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
// Mantine
import {
	Box,
	LoadingOverlay,
	NumberFormatter,
	Text,
	Tooltip,
} from '@mantine/core';

// Styles
import classes from './Measurabilities.module.css';

// I18n
import { useTranslation } from 'react-i18next';

// Constants
import { NOT_APPLICABLE } from '@config/constants/common';

// Apis

// Lodash
import isNil from 'lodash/isNil';

// Types
import { TParsedRowProject } from '@features/offsets/types';
import { getCurrencyUnit } from '@utils/currency-units';
import { convertGCO2EToTCO2EWithoutUnitFloor } from '@utils/functions';
import { getUnitLabel } from '@utils/units';

/* -------------------------------------------------------------------------- */
/*                                    Type                                    */
/* -------------------------------------------------------------------------- */
type RowExpansionProps = {
	measurabilities: any;
	isLoading: boolean;
	kpisProjects: {
		id: string;
		name: string;
		kpis?: {
			SUM_OFFSET_AMOUNT_EURO: number;
			SUM_OFFSET_AMOUNT_GCO2: number;
		};
	}[];
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

export const Measurabilities = (
	props: RowExpansionProps
) => {
	/* ******************************** Hooks ******************************* */
	const { t } = useTranslation();

	const { measurabilities, isLoading, kpisProjects } =
		props;

	const rowsMesurability =
		Array.isArray(measurabilities) &&
		measurabilities.length > 0 ? (
			measurabilities.map((item: TParsedRowProject) => {
				const soldCredit =
					kpisProjects.findIndex(
						(kpi) => kpi.id == item.id
					) !== -1
						? kpisProjects.find(
								(kpi) => kpi.id == item.id
							)?.kpis?.SUM_OFFSET_AMOUNT_GCO2
						: 0;

				return (
					<Box key={item.id} className={classes.row}>
						<Tooltip
							disabled={
								(!isNil(item) && isNil(item.name)) ||
								(!isNil(item.name) &&
									item.name.length <= 30)
							}
							label={item.name}
						>
							<Box className={classes.divition}>
								<Text className={classes.rowText}>
									{item.name}
								</Text>
							</Box>
						</Tooltip>
						<Box className={classes.divition}>
							<NumberFormatter
								className={classes.value}
								thousandSeparator=" "
								value={item.price}
								suffix={' ' + getCurrencyUnit('EUR')}
								decimalScale={2}
							/>
						</Box>
						<Box className={classes.divition}>
							<Text className={classes.rowText}>
								{kpisProjects &&
								Array.isArray(kpisProjects) &&
								kpisProjects.findIndex(
									(kpi) => kpi.id == item.id
								) !== -1
									? convertGCO2EToTCO2EWithoutUnitFloor(
											soldCredit || 0
										) +
										' ' +
										getUnitLabel('tco2e')
									: NOT_APPLICABLE}
							</Text>
						</Box>
						<Box className={classes.divition}>
							<Text className={classes.rowText}>
								{item.availableStock}
							</Text>
						</Box>
					</Box>
				);
			})
		) : (
			<Box className={classes.row}>
				<Box className={classes.divition}>
					<Text className={classes.rowText}>
						{NOT_APPLICABLE}
					</Text>
				</Box>
				<Box className={classes.divition}>
					<Text className={classes.rowText}>
						{NOT_APPLICABLE}
					</Text>
				</Box>
				<Box className={classes.divition}>
					<Text className={classes.rowText}>
						{NOT_APPLICABLE}
					</Text>
				</Box>
				<Box className={classes.divition}>
					<Text className={classes.rowText}>
						{NOT_APPLICABLE}
					</Text>
				</Box>
			</Box>
		);

	/* ******************************** RENDERING ******************************* */
	return (
		<>
			{isLoading ? (
				<Box
					pos="relative"
					className={classes.containerOverlay}
				>
					<LoadingOverlay
						visible={true}
						zIndex={1000}
						overlayProps={{ radius: 'sm', blur: 2 }}
					/>
				</Box>
			) : (
				<Box className={classes.container}>
					<Box className={classes.header}>
						<Box className={classes.divition}>
							<Text className={classes.headerText}>
								{t('global.project')}
							</Text>
						</Box>
						<Box className={classes.divition}>
							<Text className={classes.headerText}>
								{t('global.pricePerTco2e')}
							</Text>
						</Box>
						<Box className={classes.divition}>
							<Text className={classes.headerText}>
								{t('global.soldCredit')}
							</Text>
						</Box>
						<Box className={classes.divition}>
							<Text className={classes.headerText}>
								{t('global.availableStock')}
							</Text>
						</Box>
					</Box>

					<Box className={classes.row}>
						{/*Mesurability projects rows */}
						<Box className={classes.rowsDivision}>
							{rowsMesurability}
						</Box>
					</Box>
				</Box>
			)}
		</>
	);
};
